import React, { useCallback, useEffect, useRef, useState } from "react";
import { Sunrise, Play, Square, RefreshCw, TrendingUp, TrendingDown, Bell, Radio } from "lucide-react";

import { api } from "@/lib/api";
import { speakForge } from "@/lib/companionVoice";


const LS_LAST_AUTOPLAY_DATE = "forge_brief_last_autoplay";


/**
 * Morning Brief card. Loads a fresh brief from /api/companion/morning-brief on mount,
 * shows the text + key stat chips, and offers Play (speak via TTS) / Stop / Regenerate.
 * Auto-plays exactly once per calendar day on first mount (localStorage-gated).
 * Hides itself if there is nothing to say AND the user has no data at all.
 */
export function MorningBriefCard() {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const speakStopRef = useRef(null);
  const autoplayedRef = useRef(false);

  const load = useCallback(async (force = false) => {
    try {
      const { data } = await api.get("/companion/morning-brief", { params: force ? { force: 1 } : {} });
      setBrief(data);
      return data;
    } catch (err) {
      console.debug("[brief] load failed", err);
      return null;
    } finally {
      setLoading(false);
      setRegenerating(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const stop = useCallback(() => {
    try { speakStopRef.current?.(); } catch { /* noop */ }
    speakStopRef.current = null;
    setPlaying(false);
  }, []);

  const play = useCallback(async (text) => {
    if (!text) return;
    stop();
    setPlaying(true);
    try {
      const handle = await speakForge(text, {
        voice: localStorage.getItem("forge_voice") || "nova",
        onEnd: () => setPlaying(false),
      });
      speakStopRef.current = handle.stop;
    } catch (err) {
      setPlaying(false);
      console.debug("[brief] speak failed", err);
    }
  }, [stop]);

  // Auto-play on first mount of the day. Only stamp the LS date AFTER the audio actually
  // starts (or falls back cleanly) so a user opening the dashboard in a background tab
  // (where browser autoplay is blocked) doesn't silently "consume" their daily auto-play.
  useEffect(() => {
    if (autoplayedRef.current || !brief?.text) return;
    const today = new Date().toISOString().slice(0, 10);
    const last = localStorage.getItem(LS_LAST_AUTOPLAY_DATE);
    if (last === today) return;
    autoplayedRef.current = true;
    // Small delay so the page settles before Forge speaks
    const t = setTimeout(async () => {
      try {
        await play(brief.text);
        localStorage.setItem(LS_LAST_AUTOPLAY_DATE, today);
      } catch {
        // autoplay blocked or TTS failed — don't stamp, let tomorrow's visit try again
      }
    }, 900);
    return () => clearTimeout(t);
  }, [brief, play]);

  useEffect(() => () => stop(), [stop]);

  if (loading || !brief) return null;

  const stats = brief.stats || {};
  const pnl = stats.total_closed_pnl_24h ?? 0;
  const pnlClass = pnl > 0 ? "text-[var(--profit)]" : pnl < 0 ? "text-[var(--loss)]" : "text-[var(--text-secondary)]";

  return (
    <section className="card-sharp p-5 mb-6" data-testid="morning-brief-card">
      <div className="flex items-center gap-2 mb-3">
        <Sunrise size={14} className="text-[var(--accent)]"/>
        <h3 className="font-display text-lg text-white flex-1">Morning brief</h3>
        {brief.cached && <span className="chip">cached</span>}
        <button data-testid="brief-regenerate"
          onClick={async () => { setRegenerating(true); await load(true); }}
          disabled={regenerating}
          title="Regenerate the brief with fresh data"
          className="p-1.5 border border-white/10 rounded hover:border-[var(--accent)] disabled:opacity-50">
          <RefreshCw size={12} className={regenerating ? "animate-spin" : ""}/>
        </button>
        {playing ? (
          <button data-testid="brief-stop"
            onClick={stop}
            className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1.5">
            <Square size={11}/> Stop
          </button>
        ) : (
          <button data-testid="brief-play"
            onClick={() => play(brief.text)}
            className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1.5">
            <Play size={11}/> Play
          </button>
        )}
      </div>

      <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-4"
        data-testid="brief-text">
        {brief.text}
      </p>

      {/* Stat chips */}
      <div className="flex flex-wrap gap-2">
        <StatChip icon={pnl >= 0 ? <TrendingUp size={11}/> : <TrendingDown size={11}/>}
          label={`${pnl >= 0 ? "+" : ""}${pnl.toFixed(2)} USD`}
          sub={`${stats.closed_trades_24h || 0} closed · 24h`}
          className={pnlClass}
          testId="brief-stat-pnl"/>
        <StatChip icon={<Radio size={11}/>}
          label={`${stats.breakouts_count || 0} breakout${stats.breakouts_count === 1 ? "" : "s"}`}
          sub="overnight · 12h"
          testId="brief-stat-breakouts"/>
        <StatChip icon={<Bell size={11}/>}
          label={`${stats.unread_alerts || 0} unread`}
          sub="alerts"
          testId="brief-stat-alerts"/>
        <StatChip label={`${stats.active_deployments || 0} active`}
          sub="bots"
          testId="brief-stat-bots"/>
        <StatChip label={`${stats.open_positions || 0} open`}
          sub="positions"
          testId="brief-stat-positions"/>
      </div>
    </section>
  );
}


function StatChip({ icon, label, sub, className = "text-white", testId }) {
  return (
    <div className="border border-white/10 rounded px-2.5 py-1.5 bg-black/30 flex items-center gap-1.5"
      data-testid={testId}>
      {icon && <span className={className}>{icon}</span>}
      <div>
        <div className={`text-[11px] font-mono ${className}`}>{label}</div>
        {sub && <div className="text-[8.5px] font-mono text-[var(--text-muted)] uppercase tracking-wide">{sub}</div>}
      </div>
    </div>
  );
}
